import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAdminAuthStore } from '../../store/adminAuthStore';
import { AdminPermissionGuard } from '../../guards/AdminPermissionGuard';
import { AdminHierarchyGuard } from '../../guards/AdminHierarchyGuard';
import { ADMIN_PERMISSIONS, firstAdminPath } from '../../config/permissions.config';
import Spinner from '../../components/ui/Spinner';
import AdminLayout from './AdminLayout';
import AdminLoginPage from './AdminLoginPage';
import AdminForbiddenPage from './AdminForbiddenPage';
import TemplateBuilderPage from './TemplateBuilderPage';
import HierarchyBuilderPage from './HierarchyBuilderPage';
import RoleBuilderPage from './RoleBuilderPage';
import AdminUserManagementPage from './AdminUserManagementPage';

/**
 * Self-contained admin application, mounted at /admin/*. It has its own auth
 * (rrms_admin_token), routing and layout, fully independent of the CMS-based app.
 */
const AdminApp: React.FC = () => {
  const { isAuthenticated, refresh, has, admin } = useAdminAuthStore();
  const [booted, setBooted] = useState(false);

  useEffect(() => {
    refresh().finally(() => setBooted(true));
  }, [refresh]);

  const permissions = admin?.permissions ?? [];
  const homePath = firstAdminPath(has, permissions) ?? '/admin/forbidden';

  if (!booted) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="login"
        element={isAuthenticated ? <Navigate to={homePath} replace /> : <AdminLoginPage />}
      />
      {isAuthenticated ? (
        <Route element={<AdminLayout />}>
          <Route
            path="templates"
            element={
              <AdminPermissionGuard permission={ADMIN_PERMISSIONS.templates}>
                <TemplateBuilderPage />
              </AdminPermissionGuard>
            }
          />
          <Route
            path="hierarchy"
            element={
              <AdminHierarchyGuard>
                <HierarchyBuilderPage />
              </AdminHierarchyGuard>
            }
          />
          <Route
            path="roles"
            element={
              <AdminPermissionGuard permission={ADMIN_PERMISSIONS.roles}>
                <RoleBuilderPage />
              </AdminPermissionGuard>
            }
          />
          <Route
            path="users"
            element={
              <AdminPermissionGuard permission={ADMIN_PERMISSIONS.users}>
                <AdminUserManagementPage />
              </AdminPermissionGuard>
            }
          />
          <Route path="forbidden" element={<AdminForbiddenPage />} />
          <Route index element={<Navigate to={homePath} replace />} />
          <Route path="*" element={<Navigate to={homePath} replace />} />
        </Route>
      ) : (
        <Route path="*" element={<Navigate to="/admin/login" replace />} />
      )}
    </Routes>
  );
};

export default AdminApp;
